import {all, call, delay, put, select, take, takeLatest} from 'redux-saga/effects'
import {get, isEmpty, isNil} from 'lodash'
import types from '../actions/types'
import {ZOHO_SYNC_STATUS} from 'helpers/constants'
import {generalError} from '../actions'
import {IntegrationStatus} from 'helpers/integrations'
import {loadNominalCodes} from 'actions/data'
import {getDefaultListId} from 'helpers/integrations/mailchimp'
import {snackbarMessage} from 'actions/snackbar'
import {getFileAsBytes, getFileChunks} from 'helpers/documents'
import {requestData} from 'actions/pivotTable'
import {getZohoOrganisationId, getZohoSelectedTables, getZohoStatus} from "selectors/integrations"
import * as integrationActions from '../actions/integration'
import {
  hubspotDisableFailure,
  hubspotDisableSuccess,
  hubspotEnabledFailure,
  hubspotEnabledSuccess,
  hubspotStatusFailure,
  hubspotStatusSuccess,
  hubspotUpdateDataFailure,
  hubspotUpdateDataSuccess,
  loadBaseDriveFailure,
  loadBaseDriveSuccess,
  loadBaseFolderFailure,
  loadBaseFolderSuccess,
  loadBaseSiteFailure,
  loadBaseSiteSuccess,
  loadDocumentFoldersByIdFailure,
  loadDocumentFoldersByIdSuccess,
  loadDocumentUploadUrlFailure,
  loadDrivesFailure,
  loadDrivesSuccess,
  loadHubspotContactPropertiesFailure,
  loadHubspotContactPropertiesSuccess,
  loadHubspotListsFailure,
  loadHubspotListsSuccess,
  loadIsBaseFolderSetFailure,
  loadIsBaseFolderSetSuccess,
  loadMailchimpListsFailure,
  loadMailchimpListsSuccess,
  loadMailchimpMergeFields,
  loadMailchimpMergeFieldsFailure,
  loadMailchimpMergeFieldsSuccess,
  loadSitesFailure,
  loadSitesSuccess,
  mailchimpDisableFailure,
  mailchimpDisableSuccess,
  mailchimpEnabledFailure,
  mailchimpEnabledSuccess,
  mailchimpStatusFailure,
  mailchimpStatusSuccess,
  oneDriveDisableFailure,
  oneDriveDisableSuccess,
  oneDriveStatusFailure,
  oneDriveStatusSuccess,
  quickBooksDisableFailure,
  quickBooksDisableSuccess,
  quickBooksEnabledFailure,
  quickBooksEnabledSuccess,
  quickBooksStatusFailure,
  quickBooksStatusSuccess,
  quickBooksUpdateAccountDataFailure,
  quickBooksUpdateAccountDataSuccess,
  sharePointDisableFailure,
  sharePointDisableSuccess,
  sharePointStatusFailure,
  sharePointStatusSuccess,
  shopifyDisableFailure,
  shopifyDisableSuccess,
  shopifyEnabledFailure,
  shopifyEnabledSuccess,
  shopifyStatusFailure,
  shopifyStatusSuccess,
  updateBaseDriveFailure,
  updateBaseDriveSuccess,
  updateBaseSiteFailure,
  updateBaseSiteSuccess,
  updateDocumentBaseFolderFailure,
  updateDocumentBaseFolderSuccess,
  uploadFileFailure,
  uploadFileSuccess,
  xeroDisableFailure,
  xeroDisableSuccess,
  xeroEnabledFailure,
  xeroEnabledSuccess,
  xeroStatusFailure,
  xeroStatusSuccess,
  xeroUpdateAccountDataFailure,
  xeroUpdateAccountDataSuccess,
} from 'actions/integration'
import {convertMBToBytes} from "helpers/number"
import {FILE_UPLOAD_CHUNK_SIZE_MB} from "helpers/settings"

export default (api) => {

  function* googleDriveAuthenticate() {

    try {
      if (window.popup) window.popup.close()
      const response = yield call(api.documents.authorise)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {

            const url = data.result[0].URL
            window.location.assign(url)
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem connecting to Google Drive. ' + code + ':' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(snackbarMessage(
          'Problem connecting to Google Drive. ' + e.message,
          'error',
          false))
    }
  }

  function* xeroAuthenticate() {

    try {
      if (window.popup) window.popup.close()
      const response = yield call(api.xero.authorise)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {

            const url = data.result[0].URL
            window.location.assign(url)
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem connecting to Xero. ' + code + ':' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(snackbarMessage(
          'Problem connecting to Xero. ' + e.message,
          'error',
          false))
    }
  }

  function* xeroEnabled() {
    try {
      const response = yield call(api.xero.enabled)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const status = data.result[0].status
            if (status === IntegrationStatus.ALREADY_CONNECTED) {
              yield put(xeroEnabledSuccess(status))
            } else {
              yield put(xeroEnabledFailure())
            }
          },
          function* onFail() {
            yield put(xeroEnabledFailure())
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* xeroDisable() {
    try {
      const response = yield call(api.xero.disable)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const status = data.result[0].status
            if (status === IntegrationStatus.ALLOWED_TO_CONNECT) {
              yield put(xeroDisableSuccess())
            } else {
              yield put(xeroDisableFailure())
            }
          },
          function* onFail(key, message, code) {
            yield put(xeroDisableFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* xeroStatus() {
    try {
      const response = yield call(api.xero.status)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(xeroStatusSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(xeroStatusFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* xeroUpdateAccountData() {
    try {
      const response = yield call(api.xero.updateAccountData)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(xeroUpdateAccountDataSuccess(data.result[0]))
            yield put(loadNominalCodes())
          },
          function* onFail(key, message, code) {
            yield put(xeroUpdateAccountDataFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* quickBooksAuthenticate() {

    try {
      if (window.popup) window.popup.close()
      const response = yield call(api.quickBooks.authorise)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {

            const url = data.result[0].URL
            window.location.assign(url)
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem connecting to QuickBooks. ' + code + ':' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(snackbarMessage(
          'Problem connecting to QuickBooks. ' + e.message,
          'error',
          false))
    }
  }

  function* quickBooksEnabled() {
    try {
      const response = yield call(api.quickBooks.enabled)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const status = data.result[0].status
            if (status === IntegrationStatus.ALREADY_CONNECTED) {
              yield put(quickBooksEnabledSuccess(status))
            } else {
              yield put(quickBooksEnabledFailure())
            }
          },
          function* onFail() {
            yield put(quickBooksEnabledFailure())
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* quickBooksDisable() {
    try {
      const response = yield call(api.quickBooks.disable)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const status = data.result[0].status
            if (status === IntegrationStatus.ALLOWED_TO_CONNECT) {
              yield put(quickBooksDisableSuccess())
            } else {
              yield put(quickBooksDisableFailure())
            }
          },
          function* onFail(key, message, code) {
            yield put(quickBooksDisableFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* quickBooksStatus() {
    try {
      const response = yield call(api.quickBooks.status)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(quickBooksStatusSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(quickBooksStatusFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* quickBooksUpdateAccountData() {
    try {
      const response = yield call(api.quickBooks.updateAccountData)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(quickBooksUpdateAccountDataSuccess(data.result[0]))
            yield put(loadNominalCodes())
          },
          function* onFail(key, message, code) {
            yield put(quickBooksUpdateAccountDataFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* shopifyAuthenticate({shop}) {
    try {

      if (window.popup)
        window.popup.close()

      const params = {shop}
      const response = yield call(api.shopify.authorise, params)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {

            const url = data.result[0].URL
            window.location.assign(url)
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem connecting to Mailchimp. ' + code + ':' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(snackbarMessage(
          'Problem connecting to Mailchimp. ' + e.message,
          'error',
          false))
    }
  }

  function* shopifyEnabled() {
    try {
      const response = yield call(api.shopify.enabled)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(shopifyEnabledSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(shopifyEnabledFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* shopifyDisable() {
    try {
      const response = yield call(api.shopify.disable)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(shopifyDisableSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(shopifyDisableFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* shopifyStatus() {
    try {
      const response = yield call(api.shopify.status)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(shopifyStatusSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(shopifyStatusFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* googleDriveAuthenticateCallback() {
    yield put(integrationActions.googleDriveCheckAuth())
  }

  function* googleDriveCheckAuth() {
    try {
      const response = yield call(api.documents.isAuthenticated)
      if (response.data.data.authenticated) {
        yield put(integrationActions.googleDriveCheckAuthSuccess())
      } else {
        yield put(snackbarMessage(`Error: Failed to connect`))
        yield put(integrationActions.googleDriveCheckAuthFailure())
      }
    } catch (e) {
      console.error(e)
    }
  }

  function* googleDriveDeauthenticate() {
    try {
      const response = yield call(api.documents.deauthenticate)
      if (response.data.data.authenticated) {
        yield put(snackbarMessage(`Error: Failed to disconnect`))
        yield put(integrationActions.googleDriveDeauthenticateFailure())
      } else {
        yield put(integrationActions.googleDriveDeauthenticateSuccess())
        yield put(snackbarMessage(`Google Drive disconnected`))
      }
    } catch (e) {
      console.error(e)
    }
  }


  function* googleDriveSetBase({folderId}) {
    try {
      const response = yield call(api.documents.setBaseFolder, folderId)
      if (response.data.data.problem) {
        yield put(snackbarMessage(`Error: ${response.data.data.problem}`))
        yield put(integrationActions.googleDriveSetBaseFailure())
      } else {
        yield put(integrationActions.googleDriveSetBaseSuccess())
      }
    } catch (e) {
      console.error(e)
    }
  }

  function* zohoStatus({connectIfAuthorised}) {
    try {
      const response = yield call(api.zoho.get)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const status = data.result ? data.result[0] : null
            yield put(integrationActions.zohoStatusSuccess(status))

            // Automatically return to step 1 if already authorised
            if (connectIfAuthorised && status && !status.active && status.authorised)
              yield put(integrationActions.zohoConnectStart(1))
          },
          function* onFail(key, message, code) {
            yield put(integrationActions.zohoStatusFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* zohoAuthenticate({email, region}) {

    try {
      if (window.popup) window.popup.close()
      const response = yield call(api.zoho.authorise, {email, region})

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const result = data.result[0]
            const zohoSync = get(result, 'zohoSync', null)
            const url = get(result, 'URL', null)

            yield put(integrationActions.zohoAuthenticateSuccess(zohoSync))
            yield put(integrationActions.zohoInitializeWorkspace())

            window.location.assign(url)
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem connecting to Zoho Analytics. ' + code + ':' + message,
                'error',
                false))
            yield put(integrationActions.zohoAuthenticateFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(snackbarMessage(
          'Problem connecting to Zoho Analytics. ' + e.message,
          'error',
          false))
    }
  }

  function* zohoConnectStarted({step}) {
    if (step) yield zohoConnectStepSelected({step})
  }

  function* zohoConnectStepSelected({step}) {
    try {
      switch (step) {
        case 1:
          // Selecting organisation/workspace
          yield put(integrationActions.zohoGetOrganisations())
          break

        case 2:
          // Selecting workspaces
          yield put(integrationActions.zohoGetWorkspaces())
          break

        case 3:
          // Selecting date range
          yield zohoSelectingData()
          break
      }

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* zohoSelectingData() {
    try {
      const range = yield select(state => get(getZohoStatus(state), 'range'))
      const tables = yield select(state => getZohoSelectedTables(state))

      yield put(integrationActions.zohoGetRecords(range, tables))
      yield put(integrationActions.zohoGetPlan())
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* zohoActivate({organisationId, workspaceId, workspace, range, tables}) {
    try {
      const response = yield call(
          api.zoho.update,
          {active: true, organisationId, workspaceId, workspace, range, tables}
      )

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(integrationActions.zohoActivateSuccess(data.result[0]))
            yield put(integrationActions.zohoGetSelectedTables())
          },
          function* onFail(key, message, code) {
            yield put(integrationActions.zohoActivateFailure(message, code))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* zohoDeactivate() {
    try {
      const response = yield call(api.zoho.update, {active: false})

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(integrationActions.zohoDeactivateSuccess(data.result[0]))
            yield put(integrationActions.zohoSetConnectStep(0))
          },
          function* onFail(key, message, code) {
            yield put(integrationActions.zohoDeactivateFailure(message, code))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* zohoSyncUpdate({range, tables}) {
    try {
      const response = yield call(api.zoho.update, {range, tables})

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(integrationActions.zohoUpdateSyncSuccess(data.result[0]))
            yield put(integrationActions.zohoGetSelectedTables())
          },
          function* onFail(key, message, code) {
            yield put(integrationActions.zohoUpdateSyncFailure(message, code))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* zohoGetOrganisations() {
    try {
      const response = yield call(api.zoho.getOrganisations)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(integrationActions.zohoGetOrganisationsSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(integrationActions.zohoGetOrganisationsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* zohoGetWorkspaces() {
    try {
      const organisationId = yield select(getZohoOrganisationId)
      const response = yield call(api.zoho.getWorkspaces, {organisationId})

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(integrationActions.zohoGetWorkspacesSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(integrationActions.zohoGetWorkspacesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* zohoGetRecords({range, tables}) {
    try {
      const response = yield call(api.zoho.getRecords, {range, tables})

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(integrationActions.zohoGetRecordsSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(integrationActions.zohoGetRecordsFailure(message, code))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* zohoGetPlan() {
    try {
      const organisationId = yield select(getZohoOrganisationId)
      const response = yield call(api.zoho.getPlan, {organisationId})

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(integrationActions.zohoGetPlanSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(integrationActions.zohoGetPlanFailure(message, code))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* zohoGetSelectedTables() {
    try {
      const response = yield call(api.zoho.getSelectedTables)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(integrationActions.zohoGetSelectedTablesSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(integrationActions.zohoGetSelectedTablesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  // daemon to watch for activating zoho sync
  function* zohoWatcher() {

    // Whenever the zoho status is loaded...
    while (yield take(
        [
          types.ZOHO_STATUS_SUCCESS,
          types.ZOHO_AUTHENTICATE_SUCCESS,
          types.ZOHO_ACTIVATE_SUCCESS,
          types.ZOHO_SYNC_UPDATE_SUCCESS,
        ]
    )) {

      // ...if we are in 'Activating' state...
      const status = yield select(state => state.integration.zoho.status)

      const accessToken = get(status, 'accessToken', null)
      const workspace = get(status, 'workspace', null)
      if (!isNil(accessToken) && isNil(workspace)) {
        yield put(integrationActions.zohoInitializeWorkspace())
      }

      if (status && status.status === ZOHO_SYNC_STATUS.ACTIVATING) {
        // ...wait 5 seconds and check again
        yield delay(5000)
        yield put(integrationActions.zohoStatus(false))
      }
    }
  }

  function* mailchimpAuthenticate() {
    try {
      if (window.popup) window.popup.close()
      const response = yield call(api.mailchimp.authorise)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {

            const url = data.result[0].URL
            window.location.assign(url)
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem connecting to Mailchimp. ' + code + ':' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(snackbarMessage(
          'Problem connecting to Mailchimp. ' + e.message,
          'error',
          false))
    }
  }

  function* mailchimpEnabled() {
    try {
      const response = yield call(api.mailchimp.enabled)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(mailchimpEnabledSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(mailchimpEnabledFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* mailchimpDisable() {
    try {
      const response = yield call(api.mailchimp.disable)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(mailchimpDisableSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(mailchimpDisableFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* mailchimpStatus() {
    try {
      const response = yield call(api.mailchimp.status)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(mailchimpStatusSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(mailchimpStatusFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* mailchimpLoadLists() {
    try {
      const response = yield call(api.mailchimp.lists)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const lists = data.result
            const defaultListId = getDefaultListId(lists)
            yield put(loadMailchimpListsSuccess(lists))
            yield put(loadMailchimpMergeFields(defaultListId))
          },
          function* onFail(key, message, code) {
            yield put(loadMailchimpListsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* mailchimpLoadMergeFields({listId}) {
    try {
      const params = {listId}
      const response = yield call(api.mailchimp.mergeFields, params)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadMailchimpMergeFieldsSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadMailchimpMergeFieldsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* hubspotAuthenticate() {
    try {
      if (window.popup) window.popup.close()
      const response = yield call(api.hubspot.authorise)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {

            const url = data.result[0].URL
            window.location.assign(url)
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem connecting to HubSpot. ' + code + ':' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(snackbarMessage(
          'Problem connecting to HubSpot. ' + e.message,
          'error',
          false))
    }
  }

  function* hubspotEnabled() {
    try {
      const response = yield call(api.hubspot.enabled)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(hubspotEnabledSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(hubspotEnabledFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* hubspotDisable() {
    try {
      const response = yield call(api.hubspot.disable)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(hubspotDisableSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(hubspotDisableFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* hubspotStatus() {
    try {
      const response = yield call(api.hubspot.status)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(hubspotStatusSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(hubspotStatusFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* hubspotLoadLists() {
    try {
      const response = yield call(api.hubspot.lists)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const lists = data.result
            yield put(loadHubspotListsSuccess(lists))
          },
          function* onFail(key, message, code) {
            yield put(loadHubspotListsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* hubspotContactProperties() {
    try {
      const response = yield call(api.hubspot.contactProperties)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const contactProperties = data.result
            yield put(loadHubspotContactPropertiesSuccess(contactProperties))
          },
          function* onFail(key, message, code) {
            yield put(loadHubspotContactPropertiesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* hubspotUpdateData() {
    try {
      const response = yield call(api.hubspot.updateData)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(hubspotUpdateDataSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(hubspotUpdateDataFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* oneDriveAuthenticate() {
    try {
      if (window.popup) window.popup.close()
      const response = yield call(api.oneDrive.authorise)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {

            const url = data.result[0].URL
            window.location.assign(url)
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem connecting to One Drive. ' + code + ':' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(snackbarMessage(
          'Problem connecting to One Drive. ' + e.message,
          'error',
          false))
    }
  }

  function* oneDriveDisable() {
    try {
      const response = yield call(api.oneDrive.disable)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(oneDriveDisableSuccess(data.result[0]))
            yield put(snackbarMessage(`OneDrive disconnected`))
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(`Error: Failed to disconnect`))
            yield put(oneDriveDisableFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* oneDriveStatus() {
    try {
      const response = yield call(api.oneDrive.status)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(oneDriveStatusSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(oneDriveStatusFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* sharePointAuthenticate() {
    try {
      if (window.popup) window.popup.close()

      const response = yield call(api.sharePoint.authorise)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const url = data.result[0].URL
            window.location.assign(url)
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem connecting to SharePoint. ' + code + ':' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(snackbarMessage(
          'Problem connecting to SharePoint. ' + e.message,
          'error',
          false))
    }
  }

  function* sharePointDisable() {
    try {
      const response = yield call(api.sharePoint.disable)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(sharePointDisableSuccess(data.result[0]))
            yield put(snackbarMessage(`SharePoint disconnected`))
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(`Error: Failed to disconnect`))
            yield put(sharePointDisableFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* sharePointStatus() {
    try {
      const response = yield call(api.sharePoint.status)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(sharePointStatusSuccess(data.result[0]))
          },
          function* onFail(key, message, code) {
            yield put(sharePointStatusFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* loadSites({search}) {
    try {
      const params = {
        documentProvider: "DEFAULT",
        getBaseSite: false,
        search,
      }
      const fields = {}
      const response = yield call(api.document.getSite, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadSitesSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadSitesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* loadBaseSite() {
    try {
      const params = {
        documentProvider: "DEFAULT",
        getBaseSite: true,
      }
      const fields = {}
      const response = yield call(api.document.getSite, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadBaseSiteSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadBaseSiteFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* updateBaseSite({id}) {
    try {
      const params = {
        documentProvider: "DEFAULT",
        siteId: id,
      }
      const fields = {}
      const response = yield call(api.document.updateBaseSite, params, fields)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(updateBaseSiteSuccess(true))
            yield call(loadDrives)
          },
          function* onFail(key, message, code) {
            yield put(updateBaseSiteFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* loadDrives() {
    try {
      const params = {
        documentProvider: "DEFAULT",
        getBaseDrive: false,
      }
      const fields = {}
      const response = yield call(api.document.getDrive, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadDrivesSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadDrivesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* loadBaseDrive() {
    try {
      const params = {
        documentProvider: "DEFAULT",
        getBaseDrive: true,
      }
      const fields = {}
      const response = yield call(api.document.getDrive, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadBaseDriveSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadBaseDriveFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* updateBaseDrive({id}) {
    try {
      const params = {
        documentProvider: "DEFAULT",
        driveId: id,
      }
      const fields = {}
      const response = yield call(api.document.updateBaseDrive, params, fields)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(updateBaseDriveSuccess(true))
            yield call(loadDocumentFoldersById, {folderId: null, fromRoot: true})
          },
          function* onFail(key, message, code) {
            yield put(updateBaseDriveFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* loadDocumentFoldersById({folderId, fromRoot}) {
    try {
      const params = {
        documentProvider: "DEFAULT",
        folderId,
        fromRoot,
        folderPath: "/",
      }
      const fields = {}
      const response = yield call(api.document.getFolder, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadDocumentFoldersByIdSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadDocumentFoldersByIdFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* loadBaseFolder() {
    try {
      const params = {
        documentProvider: "DEFAULT",
        fromRoot: false,
        folderId: "",
        folderPath: "",
      }
      const fields = {}
      const response = yield call(api.document.getFolder, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const removeNull = data.result.filter(item => !isNil(item))
            if (isEmpty(removeNull)) { // if data.result = [null], no base folder has been set
              yield put(loadIsBaseFolderSetSuccess(false))
              yield put(loadBaseFolderSuccess(null))
            } else {
              yield put(loadIsBaseFolderSetSuccess(true))
              yield put(loadBaseFolderSuccess(data.result[0]))
            }
          },
          function* onFail(key, message, code) {
            yield put(loadIsBaseFolderSetFailure(message, code))
            yield put(loadBaseFolderFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* updateBaseFolder({id}) {
    try {
      const params = {
        documentProvider: "DEFAULT",
        folderId: id,
      }
      const fields = {}
      const response = yield call(api.document.updateFolderBase, params, fields)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(updateDocumentBaseFolderSuccess(true))
            yield put(integrationActions.loadBaseFolder())
          },
          function* onFail(key, message, code) {
            yield put(updateDocumentBaseFolderFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* loadUploadUrl({folderPath, fileName, files, pivotTableKey}) {
    try {
      yield put(snackbarMessage('Uploading your file...', 'loading', false, false))

      const params = {
        documentProvider: "DEFAULT",
        folderPath,
        fileName,
      }
      const fields = {}
      const response = yield call(api.document.getUploadUrl, params, fields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(integrationActions.uploadFile(data.result.uploadUrl, files, pivotTableKey))
          },
          function* onFail(key, message, code) {
            yield put(loadDocumentUploadUrlFailure(message, code))
            yield put(snackbarMessage(`Your file could not be uploaded: ${message}`, 'error', false))
          }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* uploadFile({uploadUrl, files, pivotTableKey}) {
    try {
      const fileSize = get(files[0], "size")
      const chunkSize = convertMBToBytes(FILE_UPLOAD_CHUNK_SIZE_MB)

      const bytes = yield call(getFileAsBytes, files)
      const chunks = getFileChunks(bytes, chunkSize, fileSize)

      for (let i = 0; i < chunks.length; i++) {
        const {fileChunk, chunkLength, range} = chunks[i]

        const headers = {
          "Content-Length": chunkLength,
          "Content-Range": range,
        }

        const response = yield call(api.oneDrive.uploadFile, uploadUrl, fileChunk, headers)
        if (!response.ok) {
          yield put(uploadFileFailure(response.message, response.code))
          yield put(snackbarMessage(`Your file could not be uploaded: ${response.message}`, 'error', false))
          return
        }
      }

      yield put(uploadFileSuccess())
      yield put(snackbarMessage('Your file has been uploaded', 'checkmark'))
      yield put(requestData(pivotTableKey))

    } catch (e) {
      console.error(e)
    }
  }

  function* deleteDocuments({documentIds, pivotTableKey}) {
    try {
      yield put(snackbarMessage('Deleting your files...', 'loading', false, false))
      const params = {
        documentProvider: "DEFAULT",
        documentIds,
      }
      const response = yield call(api.document.delete, params)
      if (response.ok) {
        yield put(snackbarMessage('Your files have been deleted', 'checkmark'))
        yield put(requestData(pivotTableKey))
      } else {
        yield put(snackbarMessage('Your files could not be deleted: ' + response.message, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* watcher() {
    try {
      yield all([
        takeLatest(types.GOOGLE_DRIVE_AUTHENTICATE, googleDriveAuthenticate),
        takeLatest(types.GOOGLE_DRIVE_AUTHENTICATE_CALLBACK, googleDriveAuthenticateCallback),
        takeLatest(types.GOOGLE_DRIVE_CHECK_AUTH_ATTEMPT, googleDriveCheckAuth),
        takeLatest(types.GOOGLE_DRIVE_DEAUTH_ATTEMPT, googleDriveDeauthenticate),
        takeLatest(types.GOOGLE_DRIVE_SET_BASE_ATTEMPT, googleDriveSetBase),

        takeLatest(types.XERO_AUTHENTICATE, xeroAuthenticate),
        takeLatest(types.XERO_STATUS_ATTEMPT, xeroStatus),
        takeLatest(types.XERO_DISABLE_ATTEMPT, xeroDisable),
        takeLatest(types.XERO_ENABLED_ATTEMPT, xeroEnabled),
        takeLatest(types.XERO_UPDATE_ACCOUNT_DATA_ATTEMPT, xeroUpdateAccountData),

        takeLatest(types.ZOHO_STATUS_ATTEMPT, zohoStatus),
        takeLatest(types.ZOHO_AUTHENTICATE_ATTEMPT, zohoAuthenticate),
        takeLatest(types.ZOHO_ACTIVATE_ATTEMPT, zohoActivate),
        takeLatest(types.ZOHO_DEACTIVATE_ATTEMPT, zohoDeactivate),
        takeLatest(types.ZOHO_GET_ORGANISATIONS_ATTEMPT, zohoGetOrganisations),
        takeLatest(types.ZOHO_GET_WORKSPACES_ATTEMPT, zohoGetWorkspaces),
        takeLatest(types.ZOHO_GET_RECORDS_ATTEMPT, zohoGetRecords),
        takeLatest(types.ZOHO_CONNECT_START, zohoConnectStarted),
        takeLatest(types.ZOHO_SET_CONNECT_STEP, zohoConnectStepSelected),
        takeLatest(types.ZOHO_GET_PLAN_ATTEMPT, zohoGetPlan),
        takeLatest(types.ZOHO_GET_SELECTED_TABLES_ATTEMPT, zohoGetSelectedTables),
        takeLatest(types.ZOHO_CHANGE_RANGE_START, zohoSelectingData),
        takeLatest(types.ZOHO_SYNC_UPDATE_ATTEMPT, zohoSyncUpdate),

        takeLatest(types.QUICK_BOOKS_AUTHENTICATE, quickBooksAuthenticate),
        takeLatest(types.QUICK_BOOKS_ENABLED_ATTEMPT, quickBooksEnabled),
        takeLatest(types.QUICK_BOOKS_DISABLE_ATTEMPT, quickBooksDisable),
        takeLatest(types.QUICK_BOOKS_STATUS_ATTEMPT, quickBooksStatus),
        takeLatest(types.QUICK_BOOKS_UPDATE_ACCOUNT_DATA_ATTEMPT, quickBooksUpdateAccountData),

        takeLatest(types.SHOPIFY_AUTHENTICATE, shopifyAuthenticate),
        takeLatest(types.SHOPIFY_ENABLED_ATTEMPT, shopifyEnabled),
        takeLatest(types.SHOPIFY_DISABLE_ATTEMPT, shopifyDisable),
        takeLatest(types.SHOPIFY_STATUS_ATTEMPT, shopifyStatus),

        takeLatest(types.MAILCHIMP_AUTHENTICATE, mailchimpAuthenticate),
        takeLatest(types.MAILCHIMP_ENABLED_ATTEMPT, mailchimpEnabled),
        takeLatest(types.MAILCHIMP_DISABLE_ATTEMPT, mailchimpDisable),
        takeLatest(types.MAILCHIMP_STATUS_ATTEMPT, mailchimpStatus),
        takeLatest(types.MAILCHIMP_LOAD_LISTS_ATTEMPT, mailchimpLoadLists),
        takeLatest(types.MAILCHIMP_LOAD_MERGE_FIELDS_ATTEMPT, mailchimpLoadMergeFields),

        takeLatest(types.HUBSPOT_AUTHENTICATE, hubspotAuthenticate),
        takeLatest(types.HUBSPOT_ENABLED_ATTEMPT, hubspotEnabled),
        takeLatest(types.HUBSPOT_DISABLE_ATTEMPT, hubspotDisable),
        takeLatest(types.HUBSPOT_STATUS_ATTEMPT, hubspotStatus),
        takeLatest(types.HUBSPOT_LOAD_LISTS_ATTEMPT, hubspotLoadLists),
        takeLatest(types.HUBSPOT_LOAD_CONTACT_PROPERTIES_ATTEMPT, hubspotContactProperties),
        takeLatest(types.HUBSPOT_UPDATE_DATA_ATTEMPT, hubspotUpdateData),

        takeLatest(types.ONE_DRIVE_AUTHENTICATE, oneDriveAuthenticate),
        takeLatest(types.ONE_DRIVE_DISABLE_ATTEMPT, oneDriveDisable),
        takeLatest(types.ONE_DRIVE_STATUS_ATTEMPT, oneDriveStatus),

        takeLatest(types.SHARE_POINT_AUTHENTICATE, sharePointAuthenticate),
        takeLatest(types.SHARE_POINT_DISABLE_ATTEMPT, sharePointDisable),
        takeLatest(types.SHARE_POINT_STATUS_ATTEMPT, sharePointStatus),

        takeLatest(types.DOCUMENTS_LOAD_SITES_ATTEMPT, loadSites),
        takeLatest(types.DOCUMENTS_LOAD_BASE_SITE_ATTEMPT, loadBaseSite),
        takeLatest(types.DOCUMENTS_UPDATE_BASE_SITE_ATTEMPT, updateBaseSite),
        takeLatest(types.DOCUMENTS_LOAD_DRIVES_ATTEMPT, loadDrives),
        takeLatest(types.DOCUMENTS_LOAD_BASE_DRIVE_ATTEMPT, loadBaseDrive),
        takeLatest(types.DOCUMENTS_UPDATE_BASE_DRIVE_ATTEMPT, updateBaseDrive),
        takeLatest(types.DOCUMENTS_LOAD_FOLDERS_BY_ID_ATTEMPT, loadDocumentFoldersById),
        takeLatest(types.DOCUMENTS_LOAD_IS_BASE_FOLDER_SET_ATTEMPT, loadBaseFolder),
        takeLatest(types.DOCUMENTS_LOAD_BASE_FOLDER_ATTEMPT, loadBaseFolder),
        takeLatest(types.DOCUMENTS_UPDATE_BASE_FOLDER_ATTEMPT, updateBaseFolder),
        takeLatest(types.DOCUMENTS_LOAD_UPLOAD_URL_ATTEMPT, loadUploadUrl),
        takeLatest(types.DOCUMENTS_UPLOAD_FILE_ATTEMPT, uploadFile),
        takeLatest(types.DOCUMENTS_DELETE_DOCUMENTS_ATTEMPT, deleteDocuments),
      ])
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  return {
    watcher,
    zohoWatcher,
    googleDriveAuthenticate,
    googleDriveAuthenticateCallback,
    googleDriveCheckAuth,
    googleDriveDeauthenticate,
    googleDriveSetBase,
    xeroAuthenticate,
    xeroDisable,
    xeroEnabled,
  }
}
